import React, { useEffect, useMemo, useState } from 'react'
import { IoIosCloseCircleOutline } from 'react-icons/io'
import api from '../../utils/api'
import { ErrorNotification, SuccessNotification } from '../../utils'
import { Permission } from '../../types/activity_user'

const formatPermissionLabel = (permission) => {
  if (!permission) return ''
  return permission.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
}

const ADMIN_MODULE = 'admin'

const isAdminModule = (moduleName) => (moduleName || '').toLowerCase() === ADMIN_MODULE

const getAllPermissionIds = (permissions = []) => permissions.map((p) => p.id)

const getAdminFullAccessPermission = (permissions = []) =>
  permissions.find((p) => isAdminModule(p.module) && p.action === Permission.FULL_ACCESS)

const isAdminFullAccessSelected = (selectedIds, permissions = []) => {
  const adminFullAccess = getAdminFullAccessPermission(permissions)
  return Boolean(adminFullAccess && selectedIds.includes(adminFullAccess.id))
}

const groupPermissionsByModule = (permissions = []) =>
  permissions.reduce((acc, permission) => {
    const moduleName = permission.module || 'other'
    if (!acc[moduleName]) acc[moduleName] = []
    acc[moduleName].push(permission)
    return acc
  }, {})

const normalizeSelectedPermissionIds = (permissionIds = [], allPermissions = []) => {
  const grouped = groupPermissionsByModule(allPermissions)
  let normalized = [...permissionIds]

  Object.values(grouped).forEach((modulePermissions) => {
    const fullAccessPermission = modulePermissions.find((p) => p.action === Permission.FULL_ACCESS)
    if (!fullAccessPermission || !normalized.includes(fullAccessPermission.id)) return

    const modulePermissionIds = modulePermissions.map((p) => p.id)
    normalized = [...new Set([...normalized, ...modulePermissionIds])]
  })

  if (isAdminFullAccessSelected(normalized, allPermissions)) {
    normalized = [...new Set([...normalized, ...getAllPermissionIds(allPermissions)])]
  }

  return normalized
}

const getModulePermissionIds = (permissions, moduleName) =>
  permissions.filter((p) => p.module === moduleName).map((p) => p.id)

const RoleFormModal = ({ isOpen, onClose, mode = 'create', role, permissions = [], onSuccess }) => {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [selectedPermissionIds, setSelectedPermissionIds] = useState([])
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    setName(role?.name || '')
    setDescription(role?.description || '')
    const initialIds = Array.isArray(role?.permissions) ? role.permissions.map((p) => p.id) : []
    setSelectedPermissionIds(normalizeSelectedPermissionIds(initialIds, permissions))
  }, [isOpen, role, permissions])

  const groupedPermissions = useMemo(() => groupPermissionsByModule(permissions), [permissions])

  const handlePermissionToggle = (permissionId) => {
    const permission = permissions.find((p) => p.id === permissionId)
    if (!permission) return

    setSelectedPermissionIds((prev) => {
      const isSelected = prev.includes(permissionId)

      if (permission.action === Permission.FULL_ACCESS) {
        if (isAdminModule(permission.module)) {
          if (isSelected) {
            return []
          }

          return getAllPermissionIds(permissions)
        }

        const modulePermissionIds = getModulePermissionIds(permissions, permission.module)

        if (isSelected) {
          return prev.filter((id) => !modulePermissionIds.includes(id))
        }

        return [...new Set([...prev, ...modulePermissionIds])]
      }

      return isSelected
        ? prev.filter((id) => id !== permissionId)
        : [...prev, permissionId]
    })
  }

  const isPermissionDisabled = (permission, modulePermissions) => {
    const adminFullAccess = getAdminFullAccessPermission(permissions)
    if (adminFullAccess && selectedPermissionIds.includes(adminFullAccess.id)) {
      return permission.id !== adminFullAccess.id
    }

    if (permission.action === Permission.FULL_ACCESS) return false

    const fullAccessPermission = modulePermissions.find((p) => p.action === Permission.FULL_ACCESS)
    return Boolean(fullAccessPermission && selectedPermissionIds.includes(fullAccessPermission.id))
  }

  const handleSubmit = async () => {
    if (!name.trim()) {
      ErrorNotification('Role name is required.')
      return
    }

    if (selectedPermissionIds.length === 0) {
      ErrorNotification('Select at least one permission.')
      return
    }

    setSubmitting(true)
    try {
      const payload = {
        name: name.trim(),
        description: description.trim(),
        permission_ids: selectedPermissionIds
      }

      if (mode === 'edit' && role?.id) {
        await api.patch(`roles/${role.id}/`, payload)
        SuccessNotification('Permission role updated successfully.')
      } else {
        await api.post('roles/', payload)
        SuccessNotification('Permission role created successfully.')
      }

      onSuccess?.()
      onClose()
    } catch (error) {
      ErrorNotification(error.response?.data?.detail || error.response?.data || error.message || 'Failed to save role.')
    } finally {
      setSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[99999] backdrop-blur-md">
      <div className="bg-white overflow-hidden dark:bg-dark rounded-lg shadow-lg w-full md:max-w-4xl relative max-h-[90vh] flex flex-col">
        <div className="flex flex-row justify-between items-center border-b border-gray-200 p-4 flex-shrink-0 dark:border-border-dark">
          <h2 className="text-xl font-bold dark:text-white">
            {mode === 'edit' ? 'Edit Permission Role' : 'Create Permission Role'}
          </h2>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
          >
            <IoIosCloseCircleOutline className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto space-y-4 p-4 relative">
          <div className="space-y-4">
            <div className="w-full space-y-2">
              <label htmlFor="role-name" className="text-sm font-medium dark:text-white">Role Name</label>
              <input
                id="role-name"
                type="text"
                placeholder="e.g Field Officer"
                className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="w-full space-y-2">
              <label htmlFor="role-description" className="text-sm font-medium dark:text-white">Description</label>
              <textarea
                id="role-description"
                rows={3}
                placeholder="Describe what this role can do"
                className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="space-y-3">
              <p className="text-sm font-medium dark:text-white">Permissions</p>
              {Object.keys(groupedPermissions).length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">No permissions available.</p>
              ) : (
                <div className="space-y-4">
                  {Object.entries(groupedPermissions).map(([moduleName, modulePermissions]) => (
                    <div key={moduleName} className="border dark:border-border-dark border-gray-200 rounded-lg p-4">
                      <p className="text-sm font-semibold text-gray-800 dark:text-white capitalize mb-3">
                        {moduleName.replace(/_/g, ' ')}
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {modulePermissions.map((permission) => {
                          const disabled = isPermissionDisabled(permission, modulePermissions)
                          return (
                          <label
                            key={permission.id}
                            className={`flex items-center gap-2 text-sm ${
                              disabled
                                ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                : 'text-gray-700 dark:text-gray-200'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedPermissionIds.includes(permission.id)}
                              disabled={disabled}
                              onChange={() => handlePermissionToggle(permission.id)}
                              className="w-4 h-4 text-yellowcolor bg-gray-100 border-gray-300 rounded focus:ring-yellowcolor disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                            <span>{formatPermissionLabel(permission.action)}</span>
                          </label>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="border-t p-4 z-50 border-gray-200 flex flex-row items-end justify-end w-full bg-white dark:bg-dark flex-shrink-0 dark:border-border-dark">
          <div className="flex flex-row items-end justify-end gap-4">
            <button
              onClick={onClose}
              className="px-6 text-sm py-2 bg-white dark:bg-dark border dark:border-border-dark dark:text-white text-black rounded-lg"
            >
              Close
            </button>
            <button
              disabled={submitting}
              onClick={handleSubmit}
              className={`px-6 text-sm py-2 bg-yellowcolor text-black rounded-lg ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {submitting ? 'Saving...' : mode === 'edit' ? 'Update Role' : 'Create Role'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RoleFormModal
